import {Item} from '@PosterWhiteboard/items/item/item.class';
import {AnimationType, type BaseItemObject, type CopyableItemStyles, type InitItemOpts, type ItemObject, type SlideItemObject, type TimelineResizeData} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {TextSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/text-slide-item.class';
import type {TransitionObject} from '@PosterWhiteboard/models/transition.class';
import {Transition} from '@PosterWhiteboard/models/transition.class';
import {getScaledManualHorizontalPadding, getScaledManualVerticalPadding} from '@PosterWhiteboard/items/item/item.library';
import type {FabricItemDimensions} from '@PosterWhiteboard/items/text-item/text-item.class';
import {getTextFromClipboard, MAX_CHARACTERS_LIMIT} from '@PosterWhiteboard/items/text-item/text-item.class';
import {AnimateItemType, AnimationSpeed, getAnimationTime, PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {ItemBorderColor, ItemControlOption} from '@PosterWhiteboard/poster/poster-item-controls';
import {ITEM_CONTROL_DIMENSIONS} from '@PosterWhiteboard/poster/poster-item-controls.types';
import {PAGE_END_TIME_TOLERANCE_SECONDS} from '@PosterWhiteboard/poster/poster.types';
import type {SlideshowSlidesObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-slides.class';
import {getSlideshowTimelineResizeBounds, MAX_SLIDE_DURATION, MIN_SLIDE_DURATION, SlideshowSlides} from '@PosterWhiteboard/items/slideshow-item/slideshow-slides.class';
import type {TimelineResizeBounds} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import type {AddItemOpts, Page} from '@PosterWhiteboard/page/page.class';
import {getIsSpellCheckEnabledFromStore} from '@Libraries/spell-check-library';
import type {Point} from '@Utils/math.util';
import {getListTypeFromPastedText, LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';
import type {ItemCoordinates, ItemData} from '@PosterWhiteboard/page/add-item.class';
import {Deferred} from '@Libraries/deferred';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import {getUniqueString} from '@Utils/string.util';
import type {ImageData, TempImageData, VideoData} from '@Libraries/add-media-library';
import {ElementDataType, getGettyAssetClassification} from '@Libraries/add-media-library';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {showItemLoading, hideItemLoading} from '@Libraries/item-loading.library';
import type {RGBA} from '@Utils/color.util';
import {getIsClipboardReadSupported} from '@Libraries/clipboard-library';
import {closeTextSelectionPopup, handleTextSelectionPopupMenu, shouldShowTextSelectionPopup} from '@Libraries/text-selection-library';
import type {SlideItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';
import {DEFAULT_SLIDE_DURATION, SLIDESHOW_MIN_SCALED_DIMENSION} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';
import {validateText} from '@PosterWhiteboard/libraries/text.library';
import {createSlideItemFromObject} from '@PosterWhiteboard/items/item/item-factory';
import type {FabricObject, ObjectEvents, TPointerEvent} from '@postermywall/fabricjs-2';
import {Canvas, FixedLayout, Group, LayoutManager, Textbox} from '@postermywall/fabricjs-2';
import {addItemsToGroupWithOriginalScale} from '@Utils/fabric.util';
import type {DeepPartial} from '@/global';
import {hideCustomControls, unhideCustomControls} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {getOutroForIntroAnimation, isBlockAnimation} from '@PosterWhiteboard/animation/animation.class';
import {IntroAnimation} from '@PosterWhiteboard/animation/intro-animation.class';
import {OutroAnimation} from '@PosterWhiteboard/animation/outro-animation.class';
import {quantizeTimeSeconds} from '@Utils/math.util';

/**
 * saves the list style from last pasted text
 */
let pastedTextListType = LIST_TYPES.NONE;


/**
 * Minimum scaled dimension of the animation item allowed.
 */

interface UpdateTextProps {
  slideWidth: number;
  slideHeight: number;
  groupCoordinates: ItemCoordinates;
  text: string;
  slideId: string;
  newVerticalPadding: number;
}

enum SlideshowTransitionDurations {
  LOWER_SPEED = 1.3,
  LOW_SPEED = 0.9,
  MED_SPEED = 0.5,
  HIGH_SPEED = 0.25,
  HIGHER_SPEED = 0.15,
}

export interface SlideshowItemObject extends BaseItemObject {
  slides: SlideshowSlidesObject;
  transition: TransitionObject;
  selectedSlideUID: string;
}

export class SlideshowItem extends Item {
  declare _fabricObject: Group;

  public transition = new Transition();
  public gitype = ITEM_TYPE.SLIDESHOW;
  public slides!: SlideshowSlides;
  public selectedSlideUID = '';
  public clone: Textbox | null = null;
  public enterEditModeOnMouseUp = true;
  /**
   * Sync to page clock function is cached in this variable so that it can be unmounted on item remove. Inline use of that
   * function doesn't work because of bind (https://stackoverflow.com/questions/28800850/jquery-off-is-not-unbinding-events-when-using-bind)
   */
  private readonly syncToPageClockFunction;

  private readonly syncToPageSeekFunction;

  /**
   * Is set true when slideshow's updateFabricObject is called while transitioning, then it is done after transition is complete.
   */
  protected isUpdateFabricObjectPending = false;

  /**
   * Is set true when the user stops the poster.
   */
  protected isPosterStoppedWhileTransitioning = false;

  /**
   * Is set true when the user pauses the poster or if the user changes the selected slide while poster is playing.
   */
  protected isPosterPausedOrInterruptedWhileTransitioning = false;
  /**
   * Lock stat when calculating whether to start transition or not
   */
  protected transitionLock = false;
  /**
   * Is set true while the animation is playing.
   */
  public isSlideshowAnimationPlaying = false;

  public animation: IntroAnimation | OutroAnimation | undefined = undefined;

  /**
   * The jQuery deferred function used to keep the slideshow in sync during pausing and stopping processes.
   * @type {promise}
   */
  protected dfd: Deferred | undefined = undefined;

  /**
   * Track setTimeout's return value in this variable, to keep track of when the user stopped typing.
   */
  protected textChangedTimeout = 0;
  /**
   * Track setTimeout's return value in this variable, to keep track of when spell check was called on text.
   */
  protected spellCheckTimeout = 0;

  constructor(page: Page) {
    super(page);
    this.slides = new SlideshowSlides(this);
    this.transition = new Transition();
    this.syncToPageClockFunction = this.syncSlideShow.bind(this);
    this.syncToPageSeekFunction = this.onPosterSeeked.bind(this);
  }

  override get fabricObject(): Group {
    return this._fabricObject;
  }

  public override async onItemAddedToPage(): Promise<void> {
    this.page.on('time:updated', this.syncToPageClockFunction);
    this.page.on('seek', this.syncToPageSeekFunction);

    if (this.page.isVideo()) {
      await this.seekToPageTime();
    }
  }

  public override getBorderColor(): ItemBorderColor {
    return this.isStreamingItem() ? ItemBorderColor.DYNAMIC_ITEM : ItemBorderColor.STATIC_ITEM;
  }

  protected async onEnded(): Promise<void> {
    await this.stop();
  }

  public override onMoving(): void {
    super.onMoving();
    this.getSelectedSlide().onMoving();
  }

  public override onRotating(): void {
    super.onRotating();
    this.getSelectedSlide().onRotating();
  }

  public override onRemove(): void {
    super.onRemove();
    this.page.off('time:updated', this.syncToPageClockFunction);
    this.page.off('seek', this.syncToPageSeekFunction);
    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      slide.onRemove();
    }
  }

  public override clearInProgressBackgroundRemoval(): void {
    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      slide.clearInProgressBackgroundRemoval();
    }
  }

  public async seekToPageTime(): Promise<void> {
    await this.syncSlideShow(this.page.clock.getCurrentTime());
    this.page.fabricCanvas.requestRenderAll();
  }

  protected async syncSlideShow(pageTime: number): Promise<void> {
    const pageDuration = this.page.getDuration();

    if (pageTime >= 0 && pageTime <= pageDuration) {
      void this.syncVideoSlides(pageTime);

      if (this.hasTransition()) {
        if (this.isTimeToTransitionToNextSlide(pageTime)) {
          this.startTransition();
        } else if (this.areSlideTransitionTimesOverlapping() && this.isSlideshowAnimationPlaying) {
          this.stopAnimation();
        } else if (!this.isSlideshowAnimationPlaying && !this.outroAnimation.animation.isAnimationPlaying) {
          this.updateSelectedSlideForTime(pageTime);
          this.updateFirstSlideWithDelayVisibility(pageTime);
        }
      } else {
        this.updateSelectedSlideForTime(pageTime);
        this.updateFirstSlideWithDelayVisibility(pageTime);
      }
    } else {
      void this.stop();
    }
  }

  /**
   * If the currently selected slide is a video, syncs it according to poster time
   */
  protected async syncVideoSlides(time: number): Promise<void> {
    if (this.slides.hasVideoSlide()) {
      const selectedSlide = this.getSelectedSlide();
      if (selectedSlide.isVideoSlide()) {
        const startTime = this.slides.getStartTimeForSlide(this.selectedSlideUID);
        await selectedSlide.syncItemToTime(time - (startTime ?? 0));
        this.page.fabricCanvas.requestRenderAll();
      }
      this.stopUnselectedVideoSlides();
    }
  }

  protected updateSelectedSlideForTime(time: number): void {
    const slideIndex = this.slides.getSlideIndexForTime(time);
    const slideModel = this.slides.slidesHashMap[this.slides.slidesOrder[slideIndex]];

    if (slideModel && this.selectedSlideUID !== slideModel.uid) {
      this.setSelectedSlideAndUpdateView(slideModel.uid);
    }
  }

  protected setSelectedSlideAndUpdateView(slideId: string): void {
    this.setSelectedSlide(slideId);
    this.updateSlideVisibility();

    if (this.isSlideEditable(this.selectedSlideUID)) {
      this.setCloneProperties();
    } else {
      this.restoreDefaultState();
    }
  }

  protected isSlideEditable(slideId: string): boolean {
    const slideModel = this.slides.slidesHashMap[slideId];
    return slideModel.isTextSlide();
  }

  public setSelectedSlide(slideId: string): void {
    this.selectedSlideUID = slideId;
    this.page.poster.redux.updateReduxData();
  }

  public override getCopyableStyles(): CopyableItemStyles {
    const selectedSlide = this.getSelectedSlide();
    return {
      ...super.getCopyableStyles(),
      transition: this.transition.toObject(),
      ...selectedSlide.getCopyableStyles(),
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<SlideshowItemObject> = {};
    const selectedSlide = this.getSelectedSlide();
    if (copiedStyles.transition !== undefined) {
      stylesObj.transition = copiedStyles.transition;
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['shadow', 'border', 'flipX', 'flipY', 'scaleX', 'scaleY']);
    await selectedSlide.pasteStyles(copiedStyles);
    await this.applyPastedStyles({...commonStylesObj, ...stylesObj});
  }

  public getActiveText(): string {
    const selectedSlide = this.getSelectedSlide();
    if (selectedSlide.isTextSlide()) {
      return selectedSlide.text;
    }
    return '';
  }

  public async onPasteText(e?: Event): Promise<void> {
    if (await getIsClipboardReadSupported()) {
      e?.preventDefault();
    }
    const clipText = await getTextFromClipboard();
    if (this.clone && clipText) {
      const validatedText = validateText(clipText);
      const updatedSelection = this.clone.selectionStart;
      pastedTextListType = await getListTypeFromPastedText();
      const prevText = this.clone.text;
      const updatedText = prevText.substring(0, this.clone.selectionStart) + validatedText + prevText.substring(this.clone.selectionEnd, prevText.length);
      this.clone.selectionStart = updatedSelection + validatedText.length;
      this.clone.selectionEnd = this.clone.selectionStart;
      if (this.clone.hiddenTextarea) {
        this.clone.hiddenTextarea.selectionEnd = this.clone.selectionEnd;
      }
      this.onCanvasTextChange(updatedText);
    }
  }

  public getAnimationTimeForItem = (type: AnimationType, speed: AnimationSpeed): number => {
    const animationTime = getAnimationTime(type, speed);
    if (this.getSelectedSlide().getSlideDuration() <= animationTime) {
      return this.getSelectedSlide().getSlideDuration() - this.getSelectedSlide().getSlideDuration() / 2;
    }
    return animationTime;
  }

  public async setFrameForGeneration(frameTime: number, videoItemFrames: Record<string, string>): Promise<void> {
    if (this.hasOutroAnimation() && frameTime >= this.getPageEndTime() - this.getAnimationTimeForItem(this.outroAnimation.type, this.outroAnimation.speed)) {
      return;
    }

    this.updateSelectedSlideForTime(frameTime);
    if (this.hasIntroDelay() && this.slides.getSlideIndex(this.selectedSlideUID) === 0) {
      if (frameTime < this.onPageStartTime) {
        this.conditionallyHideFirstSlide();
      } else {
        this.slides.showFirstSlide();
      }
    }

    const selectedSlide = this.getSelectedSlide();
    if (selectedSlide.isVideoSlide()) {
      await selectedSlide.setImageFrame(videoItemFrames[selectedSlide.uid]);
    }

    let animationTime;
    let currentAnimationType: AnimateItemType = AnimateItemType.INTRO_ANIMATION;
    let isTransitioning;

    const startSlideTime = this.slides.getStartTimeForSlide(this.selectedSlideUID);

    if (startSlideTime === undefined) {
      throw new Error(`startSlideTime undefined for slide with id ${this.selectedSlideUID} of slideshow with id ${this.uid}`);
    }

    if ((!selectedSlide.isTextSlide() || (selectedSlide.isTextSlide() && !selectedSlide.isTextEmpty())) && this.hasTransition()) {
      if (this.isTransitioningOutTime(frameTime)) {
        isTransitioning = true;
        const remainingSlideTime = this.slides.getEndTimeForSlide(this.selectedSlideUID)! - frameTime;
        animationTime = this.getTransitionDuration() - remainingSlideTime;
        currentAnimationType = AnimateItemType.OUTRO_ANIMATION;
      } else if (this.isTransitioningInTime(frameTime)) {
        isTransitioning = true;
        animationTime = frameTime - startSlideTime;
        currentAnimationType = AnimateItemType.INTRO_ANIMATION;
      }
    }

    const isAnimationOutdated =
      !this.animation ||
      this.animation?.item.uid !== selectedSlide.uid ||
      (this.animation instanceof IntroAnimation && currentAnimationType === AnimateItemType.OUTRO_ANIMATION) ||
      (this.animation instanceof OutroAnimation && currentAnimationType === AnimateItemType.INTRO_ANIMATION);

    if (isAnimationOutdated && this.animation) {
      this.animation.stop();
    }

    if (isTransitioning && animationTime !== undefined) {
      if (isAnimationOutdated) {
        this.initializeAnimation(currentAnimationType);
      }
      this.animation?.setFrame(animationTime);
    }
  }

  private isTransitioningInTime(time: number): boolean {
    const startSlideTime = this.slides.getStartTimeForSlide(this.selectedSlideUID);
    if (startSlideTime === undefined) {
      throw new Error(`startSlideTime undefined for slide with id ${this.selectedSlideUID} of slideshow with id ${this.uid}`);
    }
    const slideTimeElapsed = time - startSlideTime;
    const slideIndex = this.slides.getSlideIndexForTime(time);
    return slideTimeElapsed >= 0 && slideTimeElapsed <= this.getTransitionDuration() && slideIndex !== 0;
  }

  private initializeAnimation(animationType: AnimateItemType): void {
    if (animationType === AnimateItemType.INTRO_ANIMATION) {
      this.animation = new IntroAnimation(this.getSelectedSlide(), {
        isTransition: true,
        animation: this.transition,
      });
      this.getSelectedSlide().fabricObject.set('opacity', 1);
    } else if (animationType === AnimateItemType.OUTRO_ANIMATION) {
      this.animation = new OutroAnimation(this.getSelectedSlide(), {
        isTransition: true,
        onComplete: this.hideSlide.bind(this, this.getSelectedSlide()),
        animation: {
          ...this.transition,
          type: getOutroForIntroAnimation(this.transition.type),
          direction: isBlockAnimation(this.transition.type) ? PositionType.LEFT : PositionType.RIGHT,
        },
      });
    }
  }

  public updateActiveText(text = '', timeout = 0): void {
    if (this.textChangedTimeout > 0) {
      window.clearTimeout(this.textChangedTimeout);
    }

    if (this.clone) {
      const slideId = this.selectedSlideUID;
      const slide = this.slides.slidesHashMap[slideId] as TextSlideItem;
      const cloneDimensions = {
        width: this.clone.width + this.clone.strokeWidth,
        height: this.clone.height + this.clone.strokeWidth,
      };
      const selectorHorizontalPadding = getScaledManualHorizontalPadding(this.fabricObject.scaleX);
      const selectorVerticalPadding = getScaledManualVerticalPadding(this.fabricObject.scaleY);
      const heightWithoutVerticalPadding = this.clone.height + this.clone.strokeWidth + selectorVerticalPadding;
      const newVerticalPadding = Math.max(this.fabricObject.height - heightWithoutVerticalPadding, 0);
      // caching these 2 variables before updating group dimensions as they are needed in the next step.
      const cloneCenterPoint = this.clone.getCenterPoint();
      const groupBottomLeft = this.fabricObject.aCoords.bl;
      // This is old js code and prevBulletsWidth always is 0.
      // const prevBulletsWidth = slide.list.width;
      const prevBulletsWidth = slide.getBulletsWidth();

      const doCloneDimensionsExceedModel =
        this.clone.dynamicMinWidth + this.clone.strokeWidth + selectorHorizontalPadding > this.fabricObject.width - prevBulletsWidth ||
        this.clone.height + this.clone.strokeWidth + selectorVerticalPadding > this.fabricObject.height;
      let newModelCoords: Point = {
        x: this.fabricObject.getX(),
        y: this.fabricObject.getY(),
      };

      let slideWidth = slide.fabricObject.width - prevBulletsWidth;
      let slideHeight = slide.fabricObject.height;
      let groupCoordinates: ItemCoordinates;

      // line changed from old js, seemed to be causing width issues. come back to this if revisiting clone issues.
      this.clone.width = Math.max(this.clone.dynamicMinWidth, slide.baseWidth);

      if (pastedTextListType !== LIST_TYPES.NONE && !slide.list.type) {
        const updatedList = slide.getDefaultList();
        updatedList.type = pastedTextListType;
        slide.list = updatedList;
        pastedTextListType = LIST_TYPES.NONE;
      }
      // spm_.hideSelectionTextPopUp();
      slide.setBulletPoints();
      slideWidth += slide.getBulletsWidth(this.clone);

      if (doCloneDimensionsExceedModel) {
        const newSlideDimensions = this.getUpdatedTextGroupDimensions(cloneDimensions, newVerticalPadding);
        slide.fabricObject.set(newSlideDimensions);
        this.updateGroupViewComponentDimensions();

        newModelCoords = slide.getUpdatedModelCoords(cloneCenterPoint, groupBottomLeft, this.clone) as Point;
        this.fabricObject.set({
          left: newModelCoords.x,
          top: newModelCoords.y,
        });

        groupCoordinates = {
          left: newModelCoords.x,
          top: newModelCoords.y,
        };
        slideWidth = newSlideDimensions.width;
        slideHeight = newSlideDimensions.height;
      } else {
        groupCoordinates = {left: this.fabricObject.getX(), top: this.fabricObject.getY()};
        this.fabricObject.set({
          left: this.fabricObject.getX(),
          top: this.fabricObject.getY(),
          width: this.fabricObject.width - prevBulletsWidth + slide.getBulletsWidth(this.clone),
          height: this.fabricObject.height,
        });
      }

      slide.fabricObject.set({
        width: this.fabricObject.width,
        height: this.fabricObject.height,
        left: -this.fabricObject.width / 2,
        top: -this.fabricObject.height / 2,
      });

      slide.setBulletsPosition(this.clone);
      this.clone.set(slide.getTextCloneAbsolutePosition(this.clone));
      slide.setBackgroundParams();

      const updateTextParams: UpdateTextProps = {
        slideWidth,
        slideHeight,
        groupCoordinates,
        text,
        slideId,
        newVerticalPadding,
      };

      this.textChangedTimeout = window.setTimeout(this.updateText.bind(this, updateTextParams), timeout);
      // facade_.sendNotification(postermywall.Core.UPDATE_SLIDE_TEXTAREA, {text: text});
    }
  }

  public override doesfabricObjBelongtoItem(fabricObj: FabricObject): boolean {
    return this.fabricObject === fabricObj || this.clone === fabricObj;
  }

  public restoreDefaultState(): void {
    if (this.clone) {
      this.clone.exitEditing();
      this.page.activeSelection.setActiveObject(this.fabricObject);
    }
  }

  /**
   * Keeps the 1st slide's visibility updated according to time if slideshow has intro delay.
   */
  protected updateFirstSlideWithDelayVisibility(time: number): void {
    if (this.hasIntroDelay() && this.slides.getSlideIndex(this.selectedSlideUID) === 0 && time === 0 && !this.page.poster.isPlaying() && time < this.onPageStartTime) {
      this.fabricObject.set('visible', false);
    }
  }

  /**
   * Whether to start transition to next slide or not
   */
  protected isTimeToTransitionToNextSlide(time: number): boolean {
    if (this.transitionLock) {
      return false;
    }
    this.transitionLock = true;

    // Don't start transition if
    // 1-Slides are already transitioning
    // 2-Poster is not playing
    // 3-Its the last slide and the transition intro & outro setting is off
    if (this.isSlideshowAnimationPlaying || !this.page.poster.isPlaying() || !this.slides.hasNextSlide()) {
      this.transitionLock = false;
      return false;
    }

    const endTimeForSlide = this.slides.getEndTimeForSlide(this.selectedSlideUID);
    if (endTimeForSlide) {
      const remainingSlideTime = endTimeForSlide - time;
      const startTransition = remainingSlideTime <= this.getTransitionDuration() && remainingSlideTime > 0;

      this.isSlideshowAnimationPlaying = startTransition;
      this.transitionLock = false;
      return startTransition;
    }
    return false;
  }

  /**
   * Starts the transitions out/in of the current slide to next slide.
   */
  protected startTransition(): void {
    const nextSlideIndex = this.slides.getNextSlideIndex();
    if (nextSlideIndex === undefined && !this.hasAnimation()) {
      return;
    }

    this.animation = new OutroAnimation(this.getSelectedSlide(), {
      isTransition: true,
      animation: {
        ...this.transition,
        type: getOutroForIntroAnimation(this.transition.type),
        direction: isBlockAnimation(this.transition.type) ? PositionType.LEFT : PositionType.RIGHT,
      },
      onComplete: this.onTransitionOutComplete.bind(this, nextSlideIndex),
    });

    this.animation.start();
  }

  /**
   * Is called after transition out animation is over. If animation was interuppted, it displays the current slide on the canvas.
   * If animation was not interrupted, calls the transitionIn() function to continue the animation to the next slide.
   */
  protected onTransitionOutComplete(nextSlideIndex: undefined | number): void {
    if (this.isPosterStoppedWhileTransitioning) {
      this.isSlideshowAnimationPlaying = false;
      this.isPosterStoppedWhileTransitioning = false;
      this.dfd?.resolve();
    } else if (this.isPosterPausedOrInterruptedWhileTransitioning) {
      this.setSelectedSlideAndUpdateView(this.selectedSlideUID);
      this.isSlideshowAnimationPlaying = false;
      this.isPosterPausedOrInterruptedWhileTransitioning = false;
      this.dfd?.resolve();
    } else if (nextSlideIndex) {
      this.hideSlide(this.slides.slidesHashMap[this.selectedSlideUID]);
      this.transitionIn(nextSlideIndex);
    } else {
      this.isSlideshowAnimationPlaying = false;
    }
    this.updateRenderFlagsOfSlides();
  }

  private showSlide(slideItem: SlideItem): void {
    slideItem.fabricObject.set({opacity: 1});
    showItemLoading(slideItem);
  }

  private hideSlide(slideItem: SlideItem): void {
    slideItem.fabricObject.set({opacity: 0});
    hideItemLoading(slideItem);
  }

  private updateRenderFlagsOfSlides(): void {
    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      slide.fabricObject.dirty = true;
    }
  }

  /**
   * Transitions in (animates) to the next slide.
   */
  protected transitionIn(slideIndex: number): void {
    const targetSlide = this.slides.slidesHashMap[this.slides.slidesOrder[slideIndex]];
    this.setSelectedSlide(targetSlide.uid);

    this.animation = new IntroAnimation(targetSlide, {
      isTransition: true,
      animation: this.transition,
      onComplete: this.onTransitionInComplete.bind(this),
    });
    this.showSlide(this.slides.slidesHashMap[targetSlide.uid]);
    void this.play();
    this.animation.start();
  }

  /**
   * Is called after transition in animation is over. If animation was interuppted, it displays the current slide on the canvas.
   */
  protected onTransitionInComplete(): void {
    this.isSlideshowAnimationPlaying = false;
    if (this.isUpdateFabricObjectPending) {
      this.updateFabricObject().catch(() => {});
      this.isUpdateFabricObjectPending = false;
    }
    if (this.isPosterStoppedWhileTransitioning) {
      this.isPosterStoppedWhileTransitioning = false;
      this.dfd?.resolve();
    } else if (this.isPosterPausedOrInterruptedWhileTransitioning) {
      this.isPosterPausedOrInterruptedWhileTransitioning = false;
      this.dfd?.resolve();
    }
  }

  /**
   * Returns true if the transition In and Out times of the slide overlap.
   * This is the case when the slide duration is smaller than the total transition duration (sum of transition in and out durations).
   */
  protected areSlideTransitionTimesOverlapping(): boolean {
    return (
      this.isTransitioningOutTime(this.page.clock.getCurrentTime()) &&
      this.isSlideshowAnimationPlaying &&
      !!this.animation &&
      this.animation.item.uid === this.getSelectedSlide().uid &&
      this.animation instanceof IntroAnimation
    );
  }

  /**
   * Returns true if the poster time passed is in the interval of a slide transitioning out.
   */
  protected isTransitioningOutTime(time: number): boolean {
    if (!this.slides.getSlideForId(this.selectedSlideUID)) {
      return false;
    }

    const endTimeForSlide = this.slides.getEndTimeForSlide(this.selectedSlideUID);
    if (endTimeForSlide) {
      const remainingSlideTime = endTimeForSlide - time;
      const startTransition = remainingSlideTime <= this.getTransitionDuration() && remainingSlideTime >= 0;
      const nextSlideExists = this.slides.hasNextSlide();

      return startTransition && nextSlideExists;
    }
    return false;
  }

  /**
   * Stops the unselected video slides. Playing of selected video slide is handled by checkForPausedStream() function.
   */
  public override async play(): Promise<void> {
    this.restoreDefaultState();
    this.stopUnselectedVideoSlides();
  }

  /**
   * Pause slideshow by stopping any currently running transitions.
   */
  public override async pause(): Promise<void> {
    await super.pause();
    this.dfd = new Deferred();
    const selectedSlide = this.getSelectedSlide();

    const promises = [];
    if (selectedSlide !== undefined && selectedSlide.isVideoSlide()) {
      promises.push(selectedSlide.pause());
    }
    await Promise.all(promises);

    if (this.isSlideshowAnimationPlaying) {
      this.isPosterPausedOrInterruptedWhileTransitioning = true;
      this.stopAnimation();
    } else {
      this.dfd.resolve();
    }

    return this.dfd.promise as Promise<void>;
  }

  public onPosterSeeked(): Promise<void> {
    this.dfd = new Deferred();

    if (this.isSlideshowAnimationPlaying) {
      this.isPosterPausedOrInterruptedWhileTransitioning = true;
      this.stopAnimation();
    } else {
      this.dfd.resolve();
    }
    return this.dfd.promise as Promise<void>;
  }

  /**
   * Stop slideshow and stop transition, if transitioning.
   */
  public override stop(): Promise<void> {
    void super.stop();
    return new Promise((resolve) => {
      void this.stopActiveAnimation().then(() => {
        this.setSelectedSlideAndUpdateView(this.slides.slidesOrder[0]);
        this.stopAllVideoSlides();
        resolve();
      });
    });
  }

  protected stopActiveAnimation(): Promise<void> {
    this.dfd = new Deferred();

    if (this.isSlideshowAnimationPlaying) {
      this.isPosterStoppedWhileTransitioning = true;
      this.stopAnimation();
    } else {
      this.dfd.resolve();
    }

    return this.dfd.promise as Promise<void>;
  }

  /**
   * Stops any unselected/inactive video slides.
   */
  protected stopUnselectedVideoSlides(): void {
    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      if (slide.isVideoSlide() && slide.uid !== this.selectedSlideUID) {
        slide.stopSlide();
      }
    }
  }

  protected stopAllVideoSlides(): void {
    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      if (slide.isVideoSlide()) {
        // slide.stopSlide();
        void slide.stop();
      }
    }
  }

  protected stopAnimation(): void {
    if (this.animation) {
      this.animation.stop();
    }
  }

  protected override updateItemDimensions(): void {}

  public deleteSlide(uid: string, undoable = true): void {
    if (Object.keys(this.slides.slidesHashMap).length === 1) {
      if (this.slides.slidesOrder[0] === uid) {
        this.page.poster.deleteItemById(this.uid, true);
      } else {
        console.error(`Deleting a slide which doesn't exist in slideshow item class`);
      }
    } else {
      const {slidesHashMap, slidesOrder} = this.slides.toObject();
      const indexOfItemToDelete = slidesOrder.indexOf(uid);

      if (indexOfItemToDelete !== -1) {
        slidesOrder.splice(indexOfItemToDelete, 1);
      }

      let updatedEndTime = this.getDuration();
      if (this.slides.slidesHashMap[uid]) {
        updatedEndTime -= this.slides.slidesHashMap[uid].getSlideDuration();
      }
      if (slidesHashMap[uid]) {
        delete slidesHashMap[uid];
      }
      void this.updateFromObject(
        {
          onPageEndTime: updatedEndTime,
          slides: {
            slidesHashMap,
            slidesOrder,
          },
        },
        {undoable}
      );
    }
  }

  public override isStreamingMediaItem(): boolean {
    return true;
  }

  public override async updateFromObject(
    obj: DeepPartial<SlideshowItemObject>,
    {
      updateRedux = true,
      fireModifiedEvent = true,
      undoable = true,
      replayPosterOnUpdateDone = false,
      checkForDurationUpdate = false,
    }: UpdateFromObjectOpts = {}
  ): Promise<void> {
    // Captured before the update applies — see Item.updateFromObject.
    const pageWasVideoBeforeUpdate = checkForDurationUpdate ? this.page.isVideo() : true;
    this.copyVals(obj);

    await this.updateFabricObjectFromObject(obj);

    if (obj.slides) {
      await this.slides.updateFromObject(obj.slides, {updateRedux: false, fireModifiedEvent: false, undoable: false, doInvalidate: false});
    }

    await this.invalidate();
    this.fabricObject.setCoords();

    await this.onItemUpdatedFromObject();

    // Mirrors Item.updateFromObject: a span change without a clock tick must re-gate visibility.
    this.updateItemVisibility();

    if (checkForDurationUpdate) {
      this.checkItemForPageDurationUpdate(pageWasVideoBeforeUpdate);
    }

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.fire('modified');
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
    if (replayPosterOnUpdateDone) {
      await this.page.poster.replayPage(this.page.hashedID);
    }
  }

  public override copyVals(obj: DeepPartial<SlideshowItemObject>): void {
    // Slides value is ignored n copy vals as they requrie async and only work with updatefromobject
    const {slides: _, transition, ...plainObj} = obj;
    super.copyVals(plainObj);
    if (transition) {
      this.transition.copyVals(transition);
    }
  }

  public resizeGroupItemsWidth(newWidth: number): void {
    if (this.slides.hasTextSlide()) {
      this.resizeWidthOfGroupItemsContainingText(newWidth);
    } else {
      for (const [slideId] of Object.entries(this.slides.slidesHashMap)) {
        this.slides.scaleMediaSlide(slideId);
      }
    }
    this.slides.updateSlidePositions();
  }

  protected override async onItemUpdatedFromObject(): Promise<void> {
    const promises = [];
    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      if (slide.isImageSlide()) {
        promises.push(slide.checkForUploadComplete());
      }
    }

    const responses = await Promise.allSettled(promises);
    for (const response of responses) {
      if (response.status === 'rejected') {
        if (this.page.poster.mode.isGeneration()) {
          console.log('Slideshow image slides failed on checkForUploadComplete. Details:', response.reason);
          throw response.reason;
        }
        console.error('Slideshow image slides failed on checkForUploadComplete. Details:', response);
      }
    }
  }

  public override toObject(): SlideshowItemObject {
    return {
      ...super.toObject(),
      transition: this.transition.toObject(),
      slides: this.slides.toObject(),
      selectedSlideUID: this.selectedSlideUID,
    };
  }

  public override toObjectWithNewUids(): SlideshowItemObject {
    const slideObjects: Record<string, SlideItemObject> = {};
    const uidMap = new Map<string, string>();

    for (const [oldUid, slide] of Object.entries(this.slides.slidesHashMap)) {
      const slideItemObject = slide.toObjectWithNewUids() as SlideItemObject;
      const newUid = slideItemObject.uid;
      uidMap.set(oldUid, newUid);
      slideObjects[newUid] = slideItemObject;
    }

    const slidesOrder = this.slides.slidesOrder.map((uid: string) => {
      return uidMap.get(uid) || uid;
    });

    return {...this.toObject(), uid: getUniqueString(), selectedSlideUID: '', slides: {slidesOrder, slidesHashMap: slideObjects}};
  }

  protected override async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();

    if (this.isSlideshowAnimationPlaying) {
      this.isUpdateFabricObjectPending = true;
    } else {
      if (typeof this.fabricObject.group !== 'undefined') {
        return;
      }

      await this.updateSlideshowFabricObject();
    }
  }

  protected async updateSlideshowFabricObject(): Promise<void> {
    // await this.updateSlides_();
    await this.updateSlidesFabricObject();

    this.applyTextStylesAndStrokeOnSelectedSlide();
    this.updateGroupDimensions();
    this.slides.updateSlideDimensions();
    this.slides.updateSlidePositions();
    this.setCloneProperties();

    this.updateSlideVisibility();
    if (!this.page.poster.mode.isGeneration()) {
      this.updateSelectedSlideForTime(this.page.clock.getCurrentTime());
    }
  }

  protected applyTextStylesAndStrokeOnSelectedSlide(): void {
    const selectedSlide = this.getSelectedSlide();
    if (selectedSlide.isTextSlide()) {
      this.applyTextStylesAndStrokeOnSlide(selectedSlide, selectedSlide.fabricTextbox);
    }
  }

  protected applyTextStylesAndStrokeOnClone(): void {
    const selectedSlide = this.getSelectedSlide();
    if (selectedSlide.isTextSlide() && this.clone !== null) {
      this.applyTextStylesAndStrokeOnSlide(selectedSlide, this.clone);
    }
  }

  protected applyTextStylesAndStrokeOnSlide(slide: TextSlideItem, fabricTextboxItem: Textbox): void {
    fabricTextboxItem.set(slide.textStyles.getTextStyles(fabricTextboxItem.width, fabricTextboxItem.height));
    slide.applyTextStroke(fabricTextboxItem);
  }

  protected async updateSlidesFabricObject(): Promise<void> {
    const promises = [];
    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      promises.push(slide.updateFabricObject());
    }
    await Promise.all(promises);
  }

  /**
   * Update slideshow group's viewComponent and model dimensions depending upon slide dimensions
   */
  protected updateGroupDimensions(): void {
    this.updateGroupViewComponentDimensions();
  }

  protected getFabricObjectForItem(opts: InitItemOpts = {}): Promise<Group> {
    return new Promise((resolve) => {
      const obj = new Group([], {
        ...super.getCommonOptions(),
        objectCaching: false,
        useSelectedFlag: true,
        perPixelTargetFind: true,
        width: opts?.width ?? 300,
        height: opts?.height ?? 300,
        scaleX: opts?.scaleX ?? 1,
        scaleY: opts?.scaleY ?? 1,
        layoutManager: new LayoutManager(new FixedLayout()),
      });

      resolve(obj);
    });
  }

  protected override initEvents(): void {
    super.initEvents();
    this.fabricObject.on('mousedown:before', this.onMouseDownBefore.bind(this));
    this.fabricObject.on('mouseup', this.onMouseUp.bind(this));
    this.fabricObject.on('mouseup:before', this.onMouseUpBefore.bind(this));
    this.fabricObject.on('deselected', this.onDeselected.bind(this));
  }

  protected override onFabricObjectModified(): void {
    const slideObjects: Record<string, any> = {};

    for (const [key, slide] of Object.entries(this.slides.slidesHashMap)) {
      slideObjects[key] = slide.toObject();
      if (slide.isTextSlide()) {
        const textSlide = slideObjects[key] as TextItemObject;
        textSlide.baseWidth = slide.getBaseWidth();
        textSlide.width = this.fabricObject.width;
        textSlide.height = this.fabricObject.height;
        textSlide.verticalPadding = slide.getVerticalPadding();
      }
    }

    this.updateFromObject(
      {
        slides: {
          slidesHashMap: slideObjects,
          slidesOrder: this.slides.slidesOrder,
        },
      },
      {
        undoable: false,
        updateRedux: false,
      }
    ).catch((e) => {
      console.error(e);
      console.error(`Failed to update item on fabric object modified, Details:${JSON.stringify(e)}`);
    });
  }

  public async addSlide(newSlide: SlideItem, slideIndex: number, {updateRedux = true, fireEvent = true, undoable = true}: AddItemOpts = {}): Promise<void> {
    newSlide.page = this.page;
    this.slides.addSlideToHashmap(newSlide);
    this.slides.slidesOrder.splice(slideIndex, 0, newSlide.uid);
    await newSlide.onItemAddedToPage();
    this.addSlideToGroupWithOriginalScale(newSlide.fabricObject);
    this.onSlideAdded(newSlide);

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireEvent) {
      this.page.fire('modified');
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
  }

  protected onSlideAdded(slideItem: SlideItem): void {
    if (this.selectedSlideUID === '' && slideItem.uid === this.slides.slidesOrder[0]) {
      this.selectedSlideUID = slideItem.uid;
    }
    slideItem.fabricObject.set({
      left: this.fabricObject.left,
      top: this.fabricObject.top,
    });
  }

  public addSlideToGroupWithOriginalScale(newSlide: FabricObject): void {
    addItemsToGroupWithOriginalScale(this.fabricObject, [newSlide]);
  }

  protected onDeselected(): void {
    const selectedSlide = this.getSelectedSlide();
    if (selectedSlide.isTextSlide()) {
      this.page.spellCheck.clearSquigglyLineStyle(selectedSlide.fabricTextbox);
    }
  }

  /**
   * Selects the previous slide
   */
  public selectPreviousSlide(): void {
    const previousSlideIndex = this.slides.getPreviousSlideIndex();
    if (previousSlideIndex !== undefined) {
      this.slides.seekToSlideIndex(previousSlideIndex);
    }
  }

  /**
   * Selects the next slide
   */
  public selectNextSlide(): void {
    const nextSlideIndex = this.slides.getNextSlideIndex();
    if (nextSlideIndex !== undefined) {
      this.slides.seekToSlideIndex(nextSlideIndex);
    }
  }

  public getMinWidth(): number {
    let maxSlideMinWidth = 0;
    let itemMinWidth = 0;

    const firstSlide = this.slides.slidesHashMap[this.slides.slidesOrder[0]];
    if (firstSlide.isTextSlide()) {
      maxSlideMinWidth = firstSlide.getMinWidth();
      itemMinWidth = maxSlideMinWidth;
    }

    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      itemMinWidth = slide.isTextSlide() ? slide.getMinWidth() : 2;

      if (itemMinWidth > maxSlideMinWidth) {
        maxSlideMinWidth = itemMinWidth;
      }
    }

    return maxSlideMinWidth;
  }

  /**
   * Returns the minimum possible height of group viewComponent according to the possible min height of slides.
   */
  public getMinHeight(): number {
    let maxSlideMinHeight = 0;
    let itemMinHeight = 0;

    const firstSlide = this.slides.slidesHashMap[this.slides.slidesOrder[0]];
    if (firstSlide.isTextSlide()) {
      maxSlideMinHeight = firstSlide.getMinHeight();
      itemMinHeight = maxSlideMinHeight;
    }

    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      itemMinHeight = slide.isTextSlide() ? slide.getMinHeight() : 2;

      if (itemMinHeight > maxSlideMinHeight) {
        maxSlideMinHeight = itemMinHeight;
      }
    }

    return maxSlideMinHeight;
  }

  /**
   * Resizes the items in the group to the new height of group
   */
  public resizeGroupItemsHeight(newHeight: number): void {
    for (const [slideId, slide] of Object.entries(this.slides.slidesHashMap)) {
      if (slide.isTextSlide()) {
        slide.fabricObject.set({
          height: newHeight,
        });

        slide.onModifyHeight();
      } else {
        this.slides.scaleMediaSlide(slideId);
      }
    }
    this.slides.updateSlidePositions();
  }

  /**
   * Resizes the items in the group containing text slides to the new width of group
   */
  protected resizeWidthOfGroupItemsContainingText(newWidth: number): void {
    let slideNewHeight = 0;
    let maxSlideHeight = 0;

    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      if (slide.isTextSlide()) {
        slide.fabricObject.set({
          width: newWidth,
        });

        slide.onModifyWidth();
        slideNewHeight = slide.fabricObject.getScaledHeight();
      }

      if (slideNewHeight > maxSlideHeight) {
        maxSlideHeight = slideNewHeight;
      }
    }

    this.fabricObject.set({height: maxSlideHeight});

    for (const [slideId, slide] of Object.entries(this.slides.slidesHashMap)) {
      if (slide.isTextSlide()) {
        slide.fabricObject.set({
          height: maxSlideHeight,
        });

        slide.onModifyHeight();
      } else {
        this.slides.scaleMediaSlide(slideId);
      }
    }
  }

  public override onScaling(): void {
    super.onScaling();

    this.checkForMinimumScale();

    const slideIds = Object.keys(this.slides.slidesHashMap);

    slideIds.forEach((slideId) => {
      this.slides.slidesHashMap[slideId].onScaling();
    });

    if (this.slides.hasTextSlide()) {
      const maxSlideDimensions = this.slides.getMaxDimensionsOfTextSlides();

      this.fabricObject.set({
        width: maxSlideDimensions.width,
        height: maxSlideDimensions.height,
      });
    }

    this.slides.updateSlideDimensions();
    this.slides.updateSlidePositions();
  }

  protected checkForMinimumScale(): void {
    let newScale;

    if (this.fabricObject.getScaledWidth() < SLIDESHOW_MIN_SCALED_DIMENSION) {
      newScale = SLIDESHOW_MIN_SCALED_DIMENSION / this.fabricObject.width;
      this.fabricObject.set({
        scaleX: newScale,
        scaleY: newScale,
      });
    }
    if (this.fabricObject.getScaledHeight() < SLIDESHOW_MIN_SCALED_DIMENSION) {
      newScale = SLIDESHOW_MIN_SCALED_DIMENSION / this.fabricObject.height;
      this.fabricObject.set({
        scaleX: newScale,
        scaleY: newScale,
      });
    }
  }

  protected onMouseDownBefore(): void {
    if (this.fabricObject === this.page.activeSelection.getActiveObject()) {
      this.beforeEnterEditMode();
    }
  }

  private onMouseUp(options: ObjectEvents['mouseup']): void {
    // Don't enter the editing mode if the mouse up event happened because of an action performed by control button
    if (
      !this.enterEditModeOnMouseUp ||
      options.transform?.actionPerformed ||
      // @ts-expect-error TODO FABRIC6
      (options.e.button && options.e.button !== 1) ||
      options.transform?.action === ItemControlOption.SLIDE_BTN_ACTION_NAME
    ) {
      this.enterEditModeOnMouseUp = true;
      return;
    }

    this.onEnterEditMode(options.e);
  }

  private onMouseUpBefore(options: ObjectEvents['mouseup:before']): void {
    if (this.page.longPressInitiated || options.target?.__corner) {
      this.enterEditModeOnMouseUp = false;
    }
  }

  public enterEditMode(e?: MouseEvent): void {
    this.beforeEnterEditMode();
    this.onEnterEditMode(e);
  }

  protected beforeEnterEditMode(): void {
    this.fabricObject.selected = true;
  }

  protected onEnterEditMode(e?: TPointerEvent): void {
    const cloneExists = !!this.clone;
    if (cloneExists) {
      // Already in edit mode — the soft keyboard was likely dismissed (e.g. via the device back button)
      // while the hidden textarea kept focus, so re-entering won't re-open it. Toggle focus instead.
      this.refocusHiddenTextarea();
      return;
    }
    if (this.fabricObject.selected && this.slides.isSlideEditable(this.selectedSlideUID)) {
      hideCustomControls(this.page.hashedID);

      void this.page.poster.pause();
      this.enterCloneEditMode(e);

      this.clone?.selectAll();
    }
  }

  /**
   * On mobile, tapping the text while already in edit mode only repositions the cursor; if the soft keyboard was
   * previously dismissed (e.g. via the device back button) the still-focused hidden textarea won't re-summon it.
   * Toggle focus so the keyboard re-opens, leaving the clone and the current selection untouched.
   */
  private onCloneMouseUp(): void {
    if (!isEditorMobileVariant() || !this.clone || !this.page.canvasPanOnTextEdit.isKeyboardHidden()) {
      return;
    }
    this.refocusHiddenTextarea();
  }

  /**
   * Forces a focus change on the editing clone's hidden textarea so the mobile soft keyboard re-opens.
   * Fabric's `blur` handler only aborts the cursor animation (it does not exit editing), so the clone and
   * its selection survive the toggle; the cursor animation is restarted afterwards.
   */
  private refocusHiddenTextarea(): void {
    const hiddenTextarea = this.clone?.hiddenTextarea;
    if (!hiddenTextarea) {
      return;
    }
    hiddenTextarea.blur();
    hiddenTextarea.focus();
    this.clone?.initDelayedCursor(true);
  }

  private enterCloneEditMode(e?: TPointerEvent): void {
    const canvas = this.page.fabricCanvas;
    const textSlideItem = this.slides.slidesHashMap[this.selectedSlideUID] as TextSlideItem;
    const textSlideFabricObejct = textSlideItem.fabricTextbox;
    const index = canvas.getObjects().indexOf(this.fabricObject);

    this.clone = new Textbox(textSlideFabricObejct.text ?? '', {});
    this.clone.__PMWID = this.uid;
    this.clone.padding = 0;

    textSlideFabricObejct.set({opacity: 0});
    canvas.add(this.clone);
    canvas.moveObjectTo(this.clone, index + 1);
    if (canvas instanceof Canvas) {
      canvas.setActiveObject(this.clone);
    }

    this.clone.on('editing:entered', this.onTextEditModeEntered.bind(this));
    this.clone.on('editing:exited', this.onEditingExited.bind(this));

    this.clone.enterEditing(e);
    this.setCloneProperties();
    this.page.canvasPanOnTextEdit.beforeTextEditEntered();
    const cloneHiddenTextarea = this.clone.hiddenTextarea;
    if (cloneHiddenTextarea) {
      cloneHiddenTextarea.addEventListener('paste', (event) => {
        void this.onPasteText(event);
      });
      cloneHiddenTextarea.addEventListener('input', this.onTextInput.bind(this));
    }

    this.clone.on('changed', () => {
      this.onCanvasTextChange(this.clone?.text);
    });
    this.clone.on('selection:changed', this.onSelectionChanged.bind(this));
    this.clone.on('mouseup', this.onCloneMouseUp.bind(this));
  }

  private onTextInput(): void {
    setTimeout(() => {
      this.page.canvasPanOnTextEdit.scrollCanvasToText();
    }, 150);
  }

  public onSelectionChanged(): void {
    if (shouldShowTextSelectionPopup(this.clone)) {
      handleTextSelectionPopupMenu(this.clone);
    } else if (getIsSpellCheckEnabledFromStore() && this.clone) {
      void this.page.spellCheck.handleSuggestionPopupMenu(this.clone);
    } else {
      closeTextSelectionPopup();
    }
  }

  protected onTextEditModeEntered(): void {
    if (this.clone && this.clone.hiddenTextarea) {
      this.clone.hiddenTextarea.setAttribute('maxlength', MAX_CHARACTERS_LIMIT);
      this.page.spellCheck.checkSpell(this.clone, true).catch(() => {});
    }
  }

  protected onEditingExited(): void {
    if (this.clone) {
      this.page.fabricCanvas.remove(this.clone);
      this.clone = null;
      this.updateSlideVisibility();
      this.page.spellCheck.closeSuggestionPopUp();
      closeTextSelectionPopup();
      this.page.canvasPanOnTextEdit.afterTextEditExited();

      unhideCustomControls(this.page.hashedID);
    }
  }

  // While a text slide is being edited, its real textbox hides behind the editing
  // clone (opacity 0) and text commits are debounced — a whole-item snapshot taken
  // then would capture the slide without its text and stick (e.g. the layers-panel
  // thumbnail via useItemThumbDataURL, which regenerates on those commits). Restore
  // the textbox for the offscreen render only; the on-canvas editing state is
  // untouched. The timeline's slideshow bar snapshots slides directly and applies
  // the same restore itself (useSlideshowThumbnails.generateStaticThumbnail).
  public override toDataURL(options: {ignoreAlpha?: boolean; maxDimension?: number} = {}): string {
    const editedSlide = this.clone ? this.slides.slidesHashMap[this.selectedSlideUID] : undefined;
    if (!editedSlide?.isTextSlide()) {
      return super.toDataURL(options);
    }
    editedSlide.fabricTextbox.set({opacity: 1});
    try {
      return super.toDataURL(options);
    } finally {
      editedSlide.fabricTextbox.set({opacity: 0});
    }
  }

  /**
   * Sets the opacity of selected slide to 1 (overall slideshow's opacity) and to 0 for the remaining slides.
   */
  protected updateSlideVisibility(): void {
    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      if (slide.uid === this.selectedSlideUID) {
        this.showSlide(slide);
        this.updateSlideTextVisibility();
      } else {
        this.hideSlide(slide);
        if (slide.isTextSlide()) {
          slide.fabricTextbox.set({opacity: 1});
        }
      }
    }
  }

  /**
   * Sets the opacity of the first slide to 0 when the user has the transition intro & outro setting on,
   * so that the first slide can transition in.
   */
  protected conditionallyHideFirstSlide(): void {
    if ((this.hasAnimation() && this.hasTransition()) || this.hasIntroDelay()) {
      this.hideSlide(this.slides.slidesHashMap[this.slides.slidesOrder[0]]);
    }
  }

  /**
   * Hides the text of slide if clone is active (text is in editing state) and vice versa.
   */
  protected updateSlideTextVisibility(): void {
    const selectedSlide = this.getSelectedSlide();

    if (selectedSlide.isTextSlide() && selectedSlide.fabricTextbox) {
      if (this.clone) {
        selectedSlide.fabricTextbox.set({opacity: 0});
      } else {
        selectedSlide.fabricTextbox.set({opacity: 1});
      }
    }
  }

  public onCanvasTextChange(text = ''): void {
    this.updateActiveText(text, 500);
  }

  protected updateText(updateTextParams: UpdateTextProps): void {
    // let bulletsApplied = false;
    const slide = this.slides.slidesHashMap[updateTextParams.slideId] as TextSlideItem;
    const slidesObject = this.slides.toObject();
    const updatedTextSlideObject = slide.toObject();
    updatedTextSlideObject.text = updateTextParams.text;
    updatedTextSlideObject.width = updateTextParams.slideWidth;
    updatedTextSlideObject.height = updateTextParams.slideHeight;
    updatedTextSlideObject.verticalPadding = updateTextParams.newVerticalPadding;
    updatedTextSlideObject.textStyles = {
      ...updatedTextSlideObject.textStyles,
    };

    const scriptAndFontFamily = slide.textStyles.getLanguageScriptAndFontForText(updateTextParams.text);
    if (scriptAndFontFamily.script) {
      updatedTextSlideObject.textStyles.script = scriptAndFontFamily.script;
    }
    if (scriptAndFontFamily.fontFamily) {
      updatedTextSlideObject.textStyles.fontFamily = scriptAndFontFamily.fontFamily;
    }

    slidesObject.slidesHashMap[updatedTextSlideObject.uid] = updatedTextSlideObject;

    if (pastedTextListType !== LIST_TYPES.NONE) {
      const updatedList = slide.getDefaultList();
      updatedList.type = pastedTextListType;
      updatedTextSlideObject.list = updatedList;
      // bulletsApplied = true;
      pastedTextListType = LIST_TYPES.NONE;
    }
    void this.updateFromObject({
      left: updateTextParams.groupCoordinates.left,
      top: updateTextParams.groupCoordinates.top,
      slides: slidesObject,
      // slides: {
      //   slidesHashMap: slidesObject,
      // },
    });
    // if (bulletsApplied) {
    //   this.facade.sendNotification(postermywall.Core.REFRESH_TEXT_OPTIONS);
    // }
  }

  public getPageEndTimeForUpdatedSlideDuration = (slideId: string, updatedSlideObject: DeepPartial<SlideItemObject>): number => {
    if (updatedSlideObject.slideDuration) {
      return this.getDuration() - this.slides.slidesHashMap[slideId].getSlideDuration() + updatedSlideObject.slideDuration;
    }
    return this.getPageEndTime();
  };

  public async updateSlideFromObject(
    slideId: string,
    updatedSlideObject: DeepPartial<SlideItemObject>,
    {updateRedux = true, undoable = true}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    const slidesObject: DeepPartial<SlideshowSlidesObject> = this.slides.toObject();

    if (slidesObject.slidesHashMap) {
      slidesObject.slidesHashMap[slideId] = updatedSlideObject;
    }

    await this.updateFromObject(
      {
        slides: slidesObject,
        onPageEndTime: this.getPageEndTimeForUpdatedSlideDuration(slideId, updatedSlideObject),
      },
      {updateRedux, undoable}
    );
  }

  protected getUpdatedTextGroupDimensions(cloneDimensions: FabricItemDimensions, newVerticalPadding: number): FabricItemDimensions {
    const selectedSlide = this.slides.slidesHashMap[this.selectedSlideUID] as TextSlideItem;
    return {
      height: cloneDimensions.height + getScaledManualVerticalPadding(this.fabricObject.scaleY) + newVerticalPadding,
      width: cloneDimensions.width + getScaledManualHorizontalPadding(this.fabricObject.scaleX) + selectedSlide.getBulletsWidth(this.clone),
    };
  }

  protected updateGroupViewComponentDimensions(): void {
    if (this.slides.hasTextSlide()) {
      const dimensions = this.slides.getMaxDimensionsOfTextSlides();

      if (dimensions.width !== this.fabricObject.width) {
        this.fabricObject.set({
          width: dimensions.width,
        });
      }

      if (dimensions.height !== this.fabricObject.height) {
        this.fabricObject.set({
          height: dimensions.height,
        });
      }
    }
  }

  /**
   * Gets duration of slideshow item
   */
  public override getDuration(): number {
    let duration = this.onPageStartTime;

    for (const id of this.slides.slidesOrder) {
      duration += this.slides.slidesHashMap[id].getSlideDuration();
    }

    return duration;
  }

  /**
   * Returns the duration time of the transition according to the transition speed.
   * IMPORTANT: MAKE SURE YOU CHANGE THE PHP getTransitionDuration FUNCTION TOO
   */
  public getTransitionDuration(): number {
    switch (this.transition.speed) {
      case AnimationSpeed.LOWER:
        return SlideshowTransitionDurations.LOWER_SPEED;

      case AnimationSpeed.LOW:
        return SlideshowTransitionDurations.LOW_SPEED;

      case AnimationSpeed.MEDIUM:
        return SlideshowTransitionDurations.MED_SPEED;

      case AnimationSpeed.HIGH:
        return SlideshowTransitionDurations.HIGH_SPEED;

      case AnimationSpeed.HIGHER:
        return SlideshowTransitionDurations.HIGHER_SPEED;

      default:
        console.error('Invalid transition speed value: ');
        return SlideshowTransitionDurations.MED_SPEED;
    }
  }

  // Mirrors VideoItem's getPageEndTime override: a slideshow's span is always driven by
  // its slide durations (plus intro delay), so callers reading getPageEndTime() see the
  // slide-driven bound regardless of any stored onPageEndTime value.
  public override getPageEndTime(): number {
    return quantizeTimeSeconds(this.getDuration());
  }

  public override getTimelineResizeBounds(): TimelineResizeBounds {
    const firstSlide = this.slides.slidesHashMap[this.slides.slidesOrder[0]];
    const lastSlide = this.slides.slidesHashMap[this.slides.slidesOrder[this.slides.slidesOrder.length - 1]];
    return getSlideshowTimelineResizeBounds(
      {startTime: this.onPageStartTime, endTime: this.getPageEndTime()},
      {firstSlideDuration: firstSlide.getSlideDuration(), lastSlideDuration: lastSlide.getSlideDuration()}
    );
  }

  // A slideshow's on-page end derives from its slide durations, so a timeline resize
  // grows/shrinks the first or last slide instead of writing onPageEndTime. The edge
  // moves only by what the slide actually absorbed within its duration limits — a
  // clamped resize must shorten the bar, never translate it (group resizes pass
  // deltas beyond what the UI's single-bar calculation would allow).
  public override applyTimelineResize(data: TimelineResizeData): void {
    const originalStartTime = this.onPageStartTime;
    const originalEndTime = this.getPageEndTime();

    if (data.handle === 'left' && data.startTime !== originalStartTime) {
      const firstSlide = this.slides.slidesHashMap[this.slides.slidesOrder[0]];
      const requestedDelta = originalStartTime - data.startTime;
      const newDuration = Math.min(MAX_SLIDE_DURATION, Math.max(MIN_SLIDE_DURATION, quantizeTimeSeconds(firstSlide.getSlideDuration() + requestedDelta)));
      const absorbedDelta = newDuration - firstSlide.getSlideDuration();
      firstSlide.slideDuration = newDuration;
      this.onPageStartTime = quantizeTimeSeconds(originalStartTime - absorbedDelta);
    }

    if (data.handle === 'right' && data.endTime !== originalEndTime) {
      const lastSlide = this.slides.slidesHashMap[this.slides.slidesOrder[this.slides.slidesOrder.length - 1]];
      const requestedDelta = data.endTime - originalEndTime;
      lastSlide.slideDuration = Math.min(MAX_SLIDE_DURATION, Math.max(MIN_SLIDE_DURATION, quantizeTimeSeconds(lastSlide.getSlideDuration() + requestedDelta)));
    }
  }

  public hasSingleSlide(): boolean {
    return Object.keys(this.slides.slidesHashMap).length === 1;
  }

  public isMultiSlide(): boolean {
    return Object.keys(this.slides.slidesHashMap).length > 1;
  }

  // Every slideshow plays over time — MigrateToVersion73 dissolved the one historically
  // static case (single-slide slideshows) into plain items.
  public isStreamingItem(): boolean {
    return true;
  }

  public hasIntroDelay(): boolean {
    return this.onPageStartTime > 0;
  }

  public hasTransition(): boolean {
    return this.transition.type !== AnimationType.NONE;
  }

  public override getManualSelectorPadding(): number {
    return ITEM_CONTROL_DIMENSIONS.PMW_ITEM_LEGACY_PADDING;
  }

  /**
   * Slideshow is valid if it has a minimum of one image, video, or non empty text slide
   */
  public isValid(): boolean {
    for (const [, slide] of Object.entries(this.slides.slidesHashMap)) {
      if ((slide.isTextSlide() && slide.text) || slide.isMediaSlide()) {
        return true;
      }
    }
    return false;
  }

  public getSelectedSlide(): SlideItem {
    return this.slides.slidesHashMap[this.selectedSlideUID];
  }

  protected setCloneProperties(): void {
    const selectedSlide = this.getSelectedSlide();
    if (this.clone !== null && selectedSlide.isTextSlide()) {
      if (this.clone.hiddenTextarea) {
        this.clone.hiddenTextarea.value = selectedSlide.text;
      }

      this.clone.set({text: selectedSlide.text});
      this.applyTextStylesAndStrokeOnClone();

      const options = {
        ...this.getCommonOptions(),
        _fontSizeFraction: selectedSlide.fabricTextbox._fontSizeFraction,
        angle: selectedSlide.fabricObject.getTotalAngle(),
        scaleX: selectedSlide.fabricObject.getObjectScaling().x,
        scaleY: selectedSlide.fabricObject.getObjectScaling().y,
        ...this.getOptionsForTextClone(selectedSlide),
      };

      this.clone.set(options);
      this.clone.set(selectedSlide.getTextCloneAbsolutePosition(this.clone));
      this.page.fabricCanvas.moveObjectTo(this.clone, this.page.fabricCanvas.getObjects().indexOf(this.fabricObject) + 1);
      this.clone.setCoords();

      if (this.spellCheckTimeout > 0) {
        clearTimeout(this.spellCheckTimeout);
      }

      this.spellCheckTimeout = window.setTimeout(this.doCheckSpell.bind(this), 500);
    }
  }

  protected doCheckSpell(): void {
    // if (!postermywall.Core.isPosterGenerating && !postermywall.Core.isViewPage) {
    if (this.clone) {
      this.page.spellCheck.checkSpell(this.clone, true).catch(() => {});
    }
    // }
  }

  protected getOptionsForTextClone(textSlideItem: TextSlideItem): Record<string, any> {
    return {
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      hasControls: false,
      cursorColor: textSlideItem.getCursorColor(),
      cursorWidth: textSlideItem.fabricTextbox.cursorWidth,
      fontSize: textSlideItem.fabricTextbox.fontSize,
      width: textSlideItem.fabricTextbox.width,
      fill: textSlideItem.fabricTextbox.fill,
      shadow: textSlideItem.fabricTextbox.shadow,
      cacheExpansionFactor: textSlideItem.fabricTextbox.cacheExpansionFactor,
      hasBorders: true,
    };
  }

  public override hasEditMode(): boolean {
    return true;
  }

  /**
   * Added slides grow this slideshow's span; pull the page's other full-span content out to
   * the new end with it (mirroring AddItem's stretch on long-item adds), so a panel-driven
   * slide add doesn't leave the tail of the page without its background. Runs before the
   * slides update, so the caller's commit snapshots the stretches and the add as one undo
   * step. Manual timeline gestures deliberately don't stretch — there the user sees, and
   * owns, every bar's span — and neither does any slide add while the timeline is open.
   */
  #stretchPageToGrownSlideshow(newEndTime: number): void {
    const visualDurationBeforeGrow = this.page.getVisualDuration();
    if (visualDurationBeforeGrow <= 0 || newEndTime <= visualDurationBeforeGrow + PAGE_END_TIME_TOLERANCE_SECONDS) {
      return;
    }
    this.page.items.stretchFullSpanContentTo(visualDurationBeforeGrow, newEndTime, this.uid);
  }

  public async addTextSlide(): Promise<void> {
    const lastTextSlide = this.slides.getLastTextSlide();
    let newTextSlide;

    // TODO: Maybe skip creating a new TextSlideItem (and eventually converting it to an object) and just do it through TextSlideItemObject.
    if (lastTextSlide) {
      newTextSlide = await createSlideItemFromObject(this.page, this, {
        ...lastTextSlide.toObject(),
        text: '',
        uid: getUniqueString(),
      });
    } else {
      newTextSlide = await this.slides.getNewTextSlideItem();
    }

    const slidesObject = this.slides.toObject();
    const newSlidesOrder = slidesObject.slidesOrder;
    const newSlidesHashmap = slidesObject.slidesHashMap;
    const selectedSlideIndex = this.slides.getSlideIndex(this.selectedSlideUID);
    const targetIndex = selectedSlideIndex !== undefined ? selectedSlideIndex + 1 : 0;

    newSlidesOrder.splice(targetIndex, 0, newTextSlide.uid);
    newSlidesHashmap[newTextSlide.uid] = newTextSlide.toObject();

    const updatedOnPageEndTime = this.getDuration() + newTextSlide.getSlideDuration();
    this.#stretchPageToGrownSlideshow(updatedOnPageEndTime);
    await this.updateFromObject(
      {
        slides: {
          slidesOrder: newSlidesOrder,
          slidesHashMap: newSlidesHashmap,
        },
        onPageEndTime: updatedOnPageEndTime,
      },
      {}
    );

    this.slides.seekToSlideIndex(targetIndex);
    this.page.poster.fire('textSlide:added', newTextSlide.uid);
  }

  public prepareAndAddMediaSlides(elementsData: ItemData[]): void {
    const {allowed, droppedTypes} = this.page.poster.filterByGettyLimits(elementsData, getGettyAssetClassification);
    getPosterUi().notifyGettyLimitReached(droppedTypes);

    const imagesData: (ImageData | TempImageData)[] = [];
    const videosData: VideoData[] = [];
    allowed.forEach((data) => {
      if (data.type === ElementDataType.IMAGE) {
        imagesData.push(data);
      } else if (data.type === ElementDataType.VIDEO) {
        videosData.push(data);
      }
    });
    if (imagesData.length) {
      void this.prepareAndAddImageSlides(imagesData);
    }
    if (videosData.length) {
      void this.prepareAndAddVideoSlides(videosData);
    }
  }

  protected async prepareAndAddImageSlides(elementsData: (ImageData | TempImageData)[]): Promise<void> {
    let additionalDuration = 0;
    const orderedImageSlidesUIDs: string[] = [];
    const slidesObject = this.slides.toObject();

    let newSlidesOrder = slidesObject.slidesOrder;
    const newSlidesHashmap = slidesObject.slidesHashMap;

    const selectedSlideIndex = this.slides.getSlideIndex(this.selectedSlideUID);
    const targetIndex = selectedSlideIndex !== undefined ? selectedSlideIndex + 1 : 0;
    const lastMediaSlideDuration = this.slides.getLastMediaSlideDuration();

    for (const elementData of elementsData) {
      const slideDuration = lastMediaSlideDuration !== undefined ? lastMediaSlideDuration : DEFAULT_SLIDE_DURATION;

      additionalDuration += slideDuration;

      const slideId = 'uid' in elementData ? elementData.uid : getUniqueString();
      orderedImageSlidesUIDs.push(slideId);

      newSlidesHashmap[slideId] = {
        gitype: ITEM_TYPE.IMAGESLIDE,
        imageSource: elementData.source,
        uid: slideId,
        slideDuration,
      } as SlideItemObject;

      if ('hashedFilename' in elementData) {
        newSlidesHashmap[slideId] = {
          ...newSlidesHashmap[slideId],
          fileExtension: elementData.extension,
          hashedFilename: elementData.hashedFilename,
          isUpscaled: elementData.isUpscaled ?? false,
        };
      } else {
        newSlidesHashmap[slideId] = {
          ...newSlidesHashmap[slideId],
          uploadingImageData: {
            tempUploadingImageUID: slideId,
            dataUrl: elementData.dataUrl,
            uploadStartTime: new Date().getTime(),
            stockId: elementData.stockId,
          },
        };
      }
    }

    newSlidesOrder = newSlidesOrder.slice(0, targetIndex).concat(orderedImageSlidesUIDs, newSlidesOrder.slice(targetIndex));
    const updatedOnPageEndTime = this.getDuration() + additionalDuration;
    this.#stretchPageToGrownSlideshow(updatedOnPageEndTime);
    try {
      await this.updateFromObject(
        {
          slides: {
            slidesOrder: newSlidesOrder,
            slidesHashMap: newSlidesHashmap,
          },
          onPageEndTime: updatedOnPageEndTime,
        },
        {updateRedux: true, undoable: true}
      );
    } finally {
      this.slides.seekToSlideIndex(targetIndex);
    }
  }

  protected async prepareAndAddVideoSlides(elementsData: VideoData[]): Promise<void> {
    let additionalDuration = 0;

    const orderedVideoSlidesUIDs: string[] = [];
    const slidesObject = this.slides.toObject();

    let newSlidesOrder = slidesObject.slidesOrder;
    const newSlidesHashmap = slidesObject.slidesHashMap;

    const selectedSlideIndex = this.slides.getSlideIndex(this.selectedSlideUID);
    const targetIndex = selectedSlideIndex !== undefined ? selectedSlideIndex + 1 : 0;

    showLoading('prepareAndAddVideoSlides');
    for (const elementData of elementsData) {
      additionalDuration += elementData.duration;

      const slideId = getUniqueString();
      orderedVideoSlidesUIDs.push(slideId);

      newSlidesHashmap[slideId] = {
        gitype: ITEM_TYPE.VIDEOSLIDE,
        videoSource: elementData.source,
        fileExtension: elementData.extension,
        hashedFilename: elementData.hashedFilename,
        duration: elementData.duration,
        frameRate: elementData.frameRate,
        uid: slideId,
        slideDuration: elementData.duration,
      } as SlideItemObject;
    }

    newSlidesOrder = newSlidesOrder.slice(0, targetIndex).concat(orderedVideoSlidesUIDs, newSlidesOrder.slice(targetIndex));
    const updatedOnPageEndTime = this.getDuration() + additionalDuration;
    this.#stretchPageToGrownSlideshow(updatedOnPageEndTime);
    try {
      await this.updateFromObject(
        {
          slides: {
            slidesOrder: newSlidesOrder,
            slidesHashMap: newSlidesHashmap,
          },
          onPageEndTime: updatedOnPageEndTime,
        },
        {updateRedux: true, undoable: true}
      );
    } finally {
      this.slides.seekToSlideIndex(targetIndex);
      hideLoading('prepareAndAddVideoSlides');
    }
  }


  public override getColors(): RGBA[] {
    let colors: RGBA[] = super.getColors();
    const {slidesOrder, slidesHashMap} = this.slides;

    for (const id of slidesOrder) {
      colors = [...colors, ...slidesHashMap[id].getColors()];
    }

    return colors;
  }

  public override getFonts(): string[] {
    const slides = Object.values(this.slides.slidesHashMap);
    const fonts: string[] = [];

    for (const eachSlide of slides) {
      if (eachSlide.isTextSlide()) {
        fonts.push(eachSlide.textStyles.fontFamily);
      }
    }

    return fonts;
  }

  public async applyStylesToAllSlides(): Promise<void> {
    const newSlidesHashmap: Record<string, DeepPartial<SlideItemObject>> = this.slides.toObject().slidesHashMap;
    const activeSlide = this.getSelectedSlide();

    for (const [, slide] of Object.entries(newSlidesHashmap)) {
      if (activeSlide.uid !== slide.uid && activeSlide.gitype === slide.gitype) {
        newSlidesHashmap[slide.uid!] = activeSlide.getAppliedSidebarProps();
      }
    }

    await this.updateFromObject({
      slides: {
        slidesOrder: this.slides.slidesOrder,
        slidesHashMap: newSlidesHashmap,
      },
    });
  }

  public updateOnPageStartTime(value: number, undoable = true): void {
    const addedStartTime = value - this.onPageStartTime;
    let updatedPageEndTime: number;
    if (!value && this.slides.hasSingleSlide()) {
      updatedPageEndTime = this.page.getDuration();
    } else {
      updatedPageEndTime = this.getDuration() + addedStartTime;
    }
    void this.updateFromObject(
      {
        onPageStartTime: value,
        onPageEndTime: updatedPageEndTime,
      },
      {undoable}
    );
  }

  public getValidatedIntroDelayFromValue(value: number): number {
    if (value < 0) {
      return 0;
    }

    return Number(value.toFixed(1));
  }

  /** A double-click on a slideshow crops the slide on show, as it crops an image or video item. */
  protected override onItemDoubleClicked(): void {
    this.#enterCropModeForSelectedSlide();
  }

  #enterCropModeForSelectedSlide(): void {
    const selectedSlide = this.getSelectedSlide();
    if (!selectedSlide.isMediaSlide()) {
      return;
    }
    void this.page.poster.mediaCrop.enterCropMode(selectedSlide);
  }
}
